import { createContext, useContext } from 'react';
import { useImmerReducer } from 'use-immer';

const userInit = {
  users: {},
  edituser: {},
  updateduser: {},
  deleteUser: {},
  filteredUsers: [],
  isLoadingUsers: false,
  isEditUserOpen: false,
  isInviteUserOpen: false,
  resetUsers: false,
  accountsToRemove: [],
  userAssociatedAccounts: [],
  isLoadingUserAssociatedAccounts: false,
  defaultAccountId: null,
};

function userReducer(draft, { type, payload }) {
  switch (type) {
    case 'get': {
      const newState = {};
      for (let i = 0; i < payload.users.length; i++) {
        let user = payload.users[i];
        newState[[user.id]] = JSON.parse(JSON.stringify(user));
        newState[[user.id]]['UserRoles'] = {};
        // iterate through user roles in payload
        if (user.UserRoles.length) {
          for (let j = 0; j < user.UserRoles.length; j++) {
            let userRole = user.UserRoles[j];
            newState[[user.id]]['UserRoles'][[userRole.id]] = userRole;
          }
        }
        newState[[user.id]]['inviteStatus'] = {
          //set up the invitation status
          isInvited: user.isInvited,
          isLoading: false,
          isAccepted: user.isAccepted,
          isInviteJustSent: false,
          isRequestToJoin: user.requestToJoin,
          isError: false,
        };
      }
      draft.users = { ...newState };
      break;
    }

    case 'setFilteredUsers': {
      draft.filteredUsers = payload;
      break;
    }

    case 'setDeleteUser': {
      draft.deleteUser = payload;
      break;
    }

    case 'addUsers': {
      for (let i = 0; i < payload.users.length; i++) {
        let newUser = {};
        let user = payload.users[i];
        newUser = JSON.parse(JSON.stringify(user));
        newUser['UserRoles'] = {};
        if (user.UserRoles.length) {
          for (let j = 0; j < user.UserRoles.length; j++) {
            let userRole = user.UserRoles[j];
            newUser['UserRoles'][[userRole.id]] = userRole;
          }
        }
        newUser['inviteStatus'] = {
          isInvited: user.isInvited,
          isLoading: false,
          isAccepted: !!user.lastActive,
          isInviteJustSent: false,
          isError: false,
        };
        draft.users[[user.id]] = newUser;
      }
      break;
    }

    case 'userUpdated': {
      draft.updateduser = payload;
      break;
    }

    case 'edit': {
      let userRoles = {};
      for (let i = 0; i < payload.UserRoles.length; i++) {
        let userRole = payload.UserRoles[i];
        userRoles[[userRole.id]] = userRole;
      }
      let newUser = {
        ...payload,
        UserRoles: userRoles,
        inviteStatus: {
          isInvited: payload.isInvited,
          isLoading: false,
          isAccepted: !!payload.lastActive,
          isInviteJustSent: false,
          isError: false,
        },
      };
      draft.users[[payload.id]] = newUser;
      break;
    }

    case 'passInfoToModal': {
      draft.edituser = payload;
      break;
    }

    case 'updateEditUser': {
      draft.edituser = {
        ...draft.edituser,
        ...payload,
      };
      break;
    }

    case 'updateInviteStatus': {
      draft.users[payload.UserId]['inviteStatus'] = payload.data;
      if (typeof payload.data.isRequestToJoin !== 'undefined') {
        draft.users[payload.UserId].requestToJoin =
          payload.data.isRequestToJoin;
      }
      break;
    }

    case 'editAccountUsers': {
      for (let i = 0; i < payload.users.length; i++) {
        let newUser = {};
        let user = payload.users[i];
        newUser = JSON.parse(JSON.stringify(user));
        newUser['UserRoles'] = {};
        if (user.UserRoles.length) {
          for (let j = 0; j < user.UserRoles.length; j++) {
            let userRole = user.UserRoles[j];
            newUser['UserRoles'][[userRole.id]] = userRole;
          }
        }
        newUser['inviteStatus'] = {
          isInvited: user.isInvited,
          isLoading: false,
          isAccepted: !!user.lastActive,
          isInviteJustSent: false,
          isError: false,
        };
        draft.users[[user.id]] = newUser;
      }
      break;
    }

    case 'denyJoinRequest': {
      delete draft.users[payload];
      break;
    }
    case 'setIsLoadingUsers': {
      draft.isLoadingUsers = payload;
      break;
    }
    case 'openEditUser': {
      draft.isEditUserOpen = true;
      break;
    }
    case 'closeEditUser': {
      draft.isEditUserOpen = false;
      break;
    }
    case 'openInviteUser': {
      draft.isInviteUserOpen = true;
      break;
    }
    case 'closeInviteUser': {
      draft.isInviteUserOpen = false;
      break;
    }
    case 'setUserToPendingToDelete': {
      draft.users[payload].pendingToDelete = true;
      break;
    }
    case 'setAccountsToRemove': {
      draft.accountsToRemove = payload;
      break;
    }
    case 'setIsLoadingUserAssociatedAccounts': {
      draft.isLoadingUserAssociatedAccounts = payload;
      break;
    }
    case 'setUserAssociatedAccounts': {
      draft.userAssociatedAccounts = payload;
      break;
    }
    case 'setDefaultAccountId': {
      draft.defaultAccountId = payload;
      break;
    }
    default: {
      break;
    }
  }
}

const [UserContext, UserDispatchContext] = [
  createContext(null),
  createContext(null),
];

export default function UserProvider({ children }) {
  const [userStore, dispatch] = useImmerReducer(userReducer, userInit);

  return (
    <UserContext.Provider value={userStore}>
      <UserDispatchContext.Provider value={dispatch}>
        {children}
      </UserDispatchContext.Provider>
    </UserContext.Provider>
  );
}

export function useUserStore() {
  return useContext(UserContext);
}

export function useUserDispatch() {
  return useContext(UserDispatchContext);
}
